import {
  WASH_START_MODE_LABELS,
  WASH_UNIT_STATE_LABELS,
  WASH_UNIT_STATES,
  type WashUnitView,
} from "@station/contracts";
import { Alert, Button, Card, EmptyState, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { ClipboardPen, Play, Settings2, WashingMachine } from "lucide-react";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  ManualCycleDialog,
  RemoteStartDialog,
  UnitSettingsDialog,
} from "../../../features/wash/unit-dialogs.js";
import { useWashUnits, type WashStationOption } from "../../../features/wash/queries.js";
import {
  Field,
  UnitStateBadge,
  washSearchShape,
  WashStationPage,
  type WashStationContext,
} from "../../../features/wash/shared.js";
import {
  currentCycleView,
  describeOpeningWindow,
  formatClock,
  formatDuration,
} from "../../../features/wash/wash-values.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount, formatDateTime } from "../../../lib/format.js";

const searchSchema = z.object(washSearchShape);

export const Route = createFileRoute("/_app/yikama/uniteler")({
  validateSearch: (search) => searchSchema.parse(search),
  component: UnitsRoute,
});

/**
 * Yıkama üniteleri — canlı pano (Prompt 18 web, ADR 0018 §4, §8, §9). Ünite başına durum (boşta / çalışıyor / arızalı +
 * arıza kodu / bağlantı yok), sürmekte olan çalışma (sunucunun `live.currentCycle`'ı: açık RUNNING aralığı + eşleşen
 * başlatmanın programı ve kalan süresi; makinenin kendi tahsilatıyla başlayan çalışmada yalnızca "çalışıyor" + geçen süre),
 * bugünkü çalışma sayısı, başlatma biçimi ve alt sayaçlar. `wash.unit.status_changed` / `wash.cycle.recorded` olayları
 * (istasyon odası) panoyu tazeler. Ayar penceresi `wash.manage`, uzaktan başlatma ve elle çalışma kaydı `wash.operate`
 * ister.
 */
function UnitsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <WashStationPage title={t("menu.washUnits")} description={t("wash.units.description")}>
      {(context) => <UnitsBody context={context} />}
    </WashStationPage>
  );
}

/** Kalan süre her 15 sn'de bir yeniden hesaplanır (tuval yerine metin; ekran okuyucuyu boğmaz). */
function useNow(intervalMs = 15_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const handle = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(handle);
  }, [intervalMs]);
  return now;
}

function UnitsBody({ context }: { context: WashStationContext }): ReactElement {
  const { t } = useTranslation();
  const { station, permissions } = context;
  const units = useWashUnits(station.id);
  const [dialog, setDialog] = useState<
    | { kind: "settings"; unit: WashUnitView }
    | { kind: "start"; unit: WashUnitView }
    | { kind: "manual"; unit: WashUnitView | null }
    | null
  >(null);
  const now = useNow();

  if (units.error) return <Alert tone="danger">{errorMessage(units.error)}</Alert>;
  if (!units.data) return <SkeletonRows rows={6} />;
  const items = units.data;
  const manualUnits = items.filter(
    (unit) => unit.settings.startMode === "MANUAL" && unit.status === "ACTIVE",
  );
  const counts = Object.fromEntries(
    WASH_UNIT_STATES.map((state) => [state, items.filter((unit) => unit.live.state === state).length]),
  );
  const todayTotal = items.reduce((sum, unit) => sum + unit.live.todayCycles, 0);

  return (
    <>
      <Card className="flex flex-wrap items-center gap-x-6 gap-y-3 p-4" data-testid="wash-unit-summary">
        <dl className="flex flex-wrap gap-x-6 gap-y-2">
          {WASH_UNIT_STATES.map((state) => (
            <Field key={state} label={WASH_UNIT_STATE_LABELS[state]}>
              <span className="tabular text-lg">{formatCount(counts[state] ?? 0)}</span>
            </Field>
          ))}
          <Field label={t("wash.units.todayTotal")}>
            <span className="tabular text-lg">{formatCount(todayTotal)}</span>
          </Field>
        </dl>
        {permissions.canOperate && manualUnits.length > 0 ? (
          <Button
            variant="outline"
            className="ms-auto"
            onClick={() => setDialog({ kind: "manual", unit: null })}
          >
            <ClipboardPen aria-hidden="true" />
            {t("wash.manualCycle.open")}
          </Button>
        ) : null}
      </Card>

      {items.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<WashingMachine />}
          title={t("wash.units.emptyTitle")}
          description={t("wash.units.emptyDescription")}
        />
      ) : (
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" aria-label={t("wash.units.listLabel")}>
          {items.map((unit) => (
            <li key={unit.id}>
              <UnitCard
                unit={unit}
                now={now}
                canManage={permissions.canManage}
                canOperate={permissions.canOperate}
                onSettings={() => setDialog({ kind: "settings", unit })}
                onStart={() => setDialog({ kind: "start", unit })}
                onManual={() => setDialog({ kind: "manual", unit })}
              />
            </li>
          ))}
        </ul>
      )}

      {dialog?.kind === "settings" ? (
        <UnitSettingsDialog
          open
          onOpenChange={(open) => !open && setDialog(null)}
          unit={dialog.unit}
          station={station as WashStationOption}
        />
      ) : null}
      {dialog?.kind === "start" ? (
        <RemoteStartDialog open onOpenChange={(open) => !open && setDialog(null)} unit={dialog.unit} />
      ) : null}
      {dialog?.kind === "manual" ? (
        <ManualCycleDialog
          open
          onOpenChange={(open) => !open && setDialog(null)}
          units={manualUnits}
          defaultUnitId={dialog.unit?.id ?? null}
        />
      ) : null}
    </>
  );
}

function UnitCard({
  unit,
  now,
  canManage,
  canOperate,
  onSettings,
  onStart,
  onManual,
}: {
  unit: WashUnitView;
  now: number;
  canManage: boolean;
  canOperate: boolean;
  onSettings: () => void;
  onStart: () => void;
  onManual: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const headingId = `unit-${unit.id}`;
  const current = currentCycleView(unit.live.currentCycle, now);
  const meters = [
    unit.settings.waterMeterName ? t("wash.units.waterMeter", { name: unit.settings.waterMeterName }) : null,
    unit.settings.electricityMeterName
      ? t("wash.units.electricityMeter", { name: unit.settings.electricityMeterName })
      : null,
  ].filter((text): text is string => text !== null);
  const isActive = unit.status === "ACTIVE";
  const canStart = canOperate && isActive && unit.settings.startMode === "REMOTE";
  const canRecord = canOperate && isActive && unit.settings.startMode === "MANUAL";
  const weekdays = [1, 2, 3, 4, 5, 6, 7].map((day) => t(`wash.weekdays.${day}`));

  return (
    <Card className="grid h-full gap-3 p-4" aria-labelledby={headingId}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="grid gap-0.5">
          <h2 id={headingId} className="text-base font-semibold">
            {unit.name}
          </h2>
          <span className="text-xs text-muted-foreground">{t(`enums.washUnitType.${unit.unitType}`)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {!isActive ? (
            <StatusBadge tone="neutral">{t(`enums.assetStatus.${unit.status}`)}</StatusBadge>
          ) : null}
          <UnitStateBadge state={unit.live.state} faultCode={unit.live.faultCode} />
        </div>
      </div>
      {unit.live.since ? (
        <p className="text-xs text-muted-foreground">
          {t("wash.units.since", { at: formatDateTime(unit.live.since) })}
        </p>
      ) : null}

      {current ? (
        <div
          className="grid gap-1 rounded-md bg-info-subtle px-3 py-2 text-sm"
          data-testid="unit-current-cycle"
        >
          <span className="font-medium">
            {current.program
              ? t("wash.units.current", { program: current.program })
              : t("wash.units.currentUnknown")}
          </span>
          <span className="text-xs">
            {t("wash.units.startedAt", { at: formatClock(current.startedAt) })}
            {" · "}
            {current.remainingSeconds === null
              ? t("wash.units.elapsed", { duration: formatDuration(current.elapsedSeconds) })
              : current.overdue
                ? t("wash.units.overdue")
                : t("wash.units.remaining", { duration: formatDuration(current.remainingSeconds) })}
          </span>
          {current.program === null ? (
            <span className="text-xs text-muted-foreground">{t("wash.units.currentUnknownHint")}</span>
          ) : null}
        </div>
      ) : null}

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
        <Field label={t("wash.units.todayCycles")}>
          <span className="tabular">{formatCount(unit.live.todayCycles)}</span>
        </Field>
        <Field label={t("wash.units.lastCycle")}>{formatDateTime(unit.live.lastCycleAt)}</Field>
        <Field label={t("wash.fields.startMode")}>{WASH_START_MODE_LABELS[unit.settings.startMode]}</Field>
        <Field label={t("wash.fields.deviceRef")}>
          <span className="font-mono">{unit.settings.deviceRef ?? "—"}</span>
        </Field>
        {unit.unitType === "HAND_WASH" ? (
          <>
            <Field label={t("wash.fields.capacity")}>{formatCount(unit.settings.capacity)}</Field>
            <Field label={t("wash.fields.slotMinutes")}>
              {t("wash.units.minutes", { count: unit.settings.slotMinutes })}
            </Field>
            <Field className="col-span-2" label={t("wash.unitSettings.openingHours")}>
              {unit.settings.openingHours.length === 0 ? (
                <span className="text-muted-foreground">{t("wash.unitSettings.noOpeningHours")}</span>
              ) : (
                <span className="grid gap-0.5">
                  {unit.settings.openingHours.map((window) => (
                    <span key={`${window.weekdays.join(",")}-${window.from}`}>
                      {describeOpeningWindow(window, weekdays, t("wash.unitSettings.everyDay"))}
                    </span>
                  ))}
                </span>
              )}
            </Field>
          </>
        ) : null}
        {meters.length > 0 ? (
          <Field className="col-span-2" label={t("wash.unitSettings.meters")}>
            <span className="grid gap-0.5">
              {meters.map((text) => (
                <span key={text}>{text}</span>
              ))}
            </span>
          </Field>
        ) : null}
        <Field className="col-span-2" label={t("wash.units.programs")}>
          {unit.programs.length === 0 ? (
            <span className="text-muted-foreground">{t("wash.units.noPrograms")}</span>
          ) : (
            <span className="flex flex-wrap gap-1">
              {unit.programs.map((item) => (
                <StatusBadge key={item.programId} tone={item.isActive ? "neutral" : "warning"}>
                  {item.code}
                  {item.deviceProgramCode ? ` → ${item.deviceProgramCode}` : ""}
                </StatusBadge>
              ))}
            </span>
          )}
        </Field>
      </dl>
      {unit.settings.isDefault ? (
        <p className="text-xs text-warning">{t("wash.units.defaultSettings")}</p>
      ) : null}

      {canManage || canStart || canRecord ? (
        <div className="mt-auto flex flex-wrap gap-2 border-t pt-3">
          {canStart ? (
            <Button
              size="sm"
              onClick={onStart}
              aria-label={t("wash.remoteStart.openFor", { name: unit.name })}
            >
              <Play aria-hidden="true" />
              {t("wash.remoteStart.open")}
            </Button>
          ) : null}
          {canRecord ? (
            <Button
              size="sm"
              variant="outline"
              onClick={onManual}
              aria-label={t("wash.manualCycle.openFor", { name: unit.name })}
            >
              <ClipboardPen aria-hidden="true" />
              {t("wash.manualCycle.short")}
            </Button>
          ) : null}
          {canManage ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={onSettings}
              aria-label={t("wash.unitSettings.openFor", { name: unit.name })}
            >
              <Settings2 aria-hidden="true" />
              {t("wash.unitSettings.open")}
            </Button>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}
